import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSpecializationNames } from "../../Services/SpecializationService";

const SearchDoctorCard = ({

  id,
  avatar_doctor,
  firstname,
  lastname,
  matricule,
  experience_years,
  satisfaction_percentage,
  day_debut_work,
  day_fin_work,
  specializations,
  merged_specializations,
  primary_specialization,
  specialite,
  address_cabinet,
  available,
  time_fin_work,
  time_debut_work,
  video_fee,
  clinic_fee
}) => {
  const { t } = useTranslation();

  // Format matricule (medical degrees) as a comma-separated string.
  // Backend may send it as a JSON string (e.g. '["DO","MBBS"]') or an array.
  const formatMatricule = () => {
    if (!matricule) return '';
    let values = matricule;
    if (typeof matricule === 'string') {
      const trimmed = matricule.trim();
      if (trimmed === '') return '';
      try {
        const parsed = JSON.parse(trimmed);
        values = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        values = trimmed.split(',');
      }
    }
    if (!Array.isArray(values)) return '';
    return values
      .map((v) => (typeof v === 'string' ? v.trim() : v))
      .filter((v) => v !== '' && v != null)
      .join(', ');
  };
  
  const specializationNames = getSpecializationNames({
    specializations,
    merged_specializations,
    primary_specialization,
    specialite,
  });

  return (
    <>
      <div className="mx-auto p-4 w-full relative rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 bg-white capitalize border border-gray-200">

        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex">
          <div className="avalible">
            {available ? (
              <div className="bg-green-100 flex items-center text-green-800 text-xs font-medium rounded-full px-2 py-0.5">
                <span className="flex w-3 h-3 bg-green-500 mr-1 rounded-full"></span>
                {t("SearchDoctorCard.Available")}
              </div>
            ) : (
              <div className="bg-gray-100 flex items-center text-gray-800 text-xs font-medium rounded-full px-2 py-0.5">
                <span className="flex w-3 h-3 bg-red-500 mr-1 rounded-full"></span>
                {t("SearchDoctorCard.Not_Available")}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
          {/* Doctor Image - Left side */}
          <div className="card-img flex-shrink-0">
            <img
              src={avatar_doctor !== null ? `https://backend.famracure.com/images/doctors/${avatar_doctor}` : "/img/doc-listing.jpg"}
              className="h-[120px] w-[140px] rounded-lg object-cover"
              alt="Doctor"
            />
          </div>

          {/* Doctor Details - Right side */}
          <div className="doctor-card-detail flex-grow text-left">
            {/* Doctor Name and Verification Badge */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="doctor-name text-lg sm:text-xl font-bold text-gray-900">
                Dr. {firstname} {lastname}
              </h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 shadow-sm">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                PMDC Verified
              </span>
            </div>

            {/* Matricule (Medical Degrees) */}
            {formatMatricule() && (
              <div className="mb-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                  </svg>
                  {formatMatricule()}
                </span>
              </div>
            )}
      
            {/* Specializations */}
            {specializationNames.length > 0 && (
              <div className="specialization flex flex-wrap gap-1.5 mb-1">
                {specializationNames.map((name) => (
                  <span
                    key={name}
                    className="bg-blue-50 px-2.5 py-0.5 rounded-full text-sm font-semibold"
                    style={{ color: '#587FD9' }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            )}
            
            {/* Experience and Duration */}
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <div className="flex items-center text-gray-700 text-sm">
                <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{experience_years} years exp</span>
              </div>
              <span className="text-gray-400">•</span>
              <div className="flex items-center text-gray-700 text-sm">
                <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">15-30 min</span>
              </div>
            </div>
          </div>
        </div>
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 border-t border-gray-100 pt-4">
  <div className="consultation-option bg-blue-50 rounded-lg p-3 hover:bg-blue-100 transition-colors">
    <Link 
      to={`/bookingappointment/${id}`} 
      state={{ feeType: "video", fee: video_fee }}
      className="block"
    >
      <div className="flex items-center mb-2">
        <img
          src="/img/Layer 2.png"
          className="w-6 h-6 mr-2"
          alt="Video Icon"
        />
        <h1 className="text-blue-800 text-sm sm:text-base font-medium">Video Consultation</h1>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`w-3 h-3 mr-1 rounded-full ${available ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <p className={`${available ? 'text-green-700' : 'text-gray-700'} text-xs font-medium`}>
            {available ? 'Available today' : 'Not available'}
          </p>
        </div>
        <p className="text-blue-900 font-bold">Rs. {video_fee}</p>
      </div>
    </Link>
  </div>

  <div className="consultation-option bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
    <Link 
      to={`/bookingappointment/${id}`} 
      state={{ feeType: "clinic", fee: clinic_fee }}
      className="block"
    >
      <div className="flex items-center mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <h1 className="text-gray-800 text-sm sm:text-base font-medium">
          In-Clinic Visit
          {address_cabinet && (
            <span className="block text-xs font-normal text-gray-500 normal-case">{address_cabinet}</span>
          )}
        </h1>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`w-3 h-3 mr-1 rounded-full ${available ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <p className={`${available ? 'text-green-700' : 'text-gray-700'} text-xs font-medium`}>
            {available ? 'Available today' : 'Not available'}
          </p>
        </div>
        <p className="text-gray-900 font-bold">Rs. {clinic_fee}</p>
      </div>
    </Link>
  </div>
</div>

<div className="mt-6">
  <Link to={`/doctor/View_Profile/${id}`} className="block w-full">
    <button className="w-full py-2.5 px-4 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow">
      {t("SearchDoctorCard.View_Profile")}
    </button>
  </Link>
</div>
      </div>



    </>

  );


};

export default SearchDoctorCard;
